import "rsuite/DateRangePicker/styles/index.css";
import { useEffect, useState } from "react";
import DateRangePicker from "rsuite/DateRangePicker";

import {
  getWorklistCounters,
  getWorklistDetails,
} from "~/services/supervisor.services";

import SupervisorTable from "./table";

const WorklistComponent = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [activeData, setActiveData] = useState<string>("All");
  const [worklistData, setWorklistData] = useState<any[]>([]);
  const [status, setStatus] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isRangeSelected, setIsRangeSelected] = useState<boolean>(false);
  const [worklistCounter, setWorklistCounter] = useState<any>(null);
  const [range, setRange] = useState<any>([
    new Date(new Date().setHours(0, 0, 0, 0) - (24 * 60 * 60 * 1000)), // Start of today
    new Date(new Date()),
  ]);
  // console.log(isRangeSelected);
  const handleDateChange = (data: any) => {
    if (data != null && data.length > 0) {
      const startDate = new Date(data[0]);
      const endDate = new Date(data[1]);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      setRange([startDate, endDate]);
      setIsRangeSelected(true);
      console.log("Date range updated:", startDate, endDate);
    }
  };

  const fetchData = async (
    fromDate?: Date,
    toDate?: Date,
    activeTab: string = "All"
  ) => {
    setLoading(true);
    let from = new Date();
    let to = new Date();

    // Default to today and tomorrow if no dates provided
    from.setHours(0, 0, 0, 0); // Set to midnight
    to.setHours(0, 0, 0, 0); // Set to midnight

    // If no dates are provided, use today and tomorrow
    if (fromDate && toDate) {
      from = new Date(fromDate);
      to = new Date(toDate);
    } else {
      to.setDate(from.getDate() + 1); // Set to tomorrow
    }

    // Ensure 'to' is set to the end of tomorrow
    to.setHours(23, 59, 59, 999);
    // Convert dates to 'YYYY-MM-DD' format
    const fromStr = from.toLocaleDateString("en-CA").replace(/\//g, "-"); //from.toISOString().split("T")[0];
    const toStr = to.toISOString().split("T")[0];

    console.log("Fetching data with fromDate:", fromStr, "toDate:", toStr);
    const { data, status } = await getWorklistDetails(fromStr, toStr);
    if (status) {
      console.log("Data received from API:", data);
      if ((data as any)?.data.length === 0) {
        setWorklistData([]);
        setStatus({});
        setActiveData("All");
      } else {
        const allData = (data as any)?.data;
        setWorklistData(allData);

        const allStatus: any = { All: allData };
        allData.forEach((d: any) => {
          if (d.Status in allStatus) {
            allStatus[d.Status].push(d);
          } else {
            allStatus[d.Status] = [d];
          }
        });
        setStatus(allStatus);

        const filteredData =
          activeTab === "All" ? allData : allStatus[activeTab] || [];
        setWorklistData(filteredData);
        setActiveData(activeTab);
      }
    }
    setLoading(false);
  };

  const fetchCounterData = async () => {
    const { data, status } = await getWorklistCounters();
    if (status) {
      setWorklistCounter((data as any)?.data);
      console.log("Counter data received:", data);
    }
  };

  useEffect(() => {
    setActiveTab(0);
    setActiveData("All");
    setLoading(true);
    void fetchData(range[0], range[1], "All").finally(() => setLoading(false));
    void fetchCounterData();
  }, [range]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      void fetchCounterData();
    }, 120000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab.toString());
  }, [activeTab]);

  return (
    <div className="">
      {worklistData != null && worklistCounter != null && !loading ? (
        <>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-2xl font-semibold text-orange-500">
                Worklist
              </h4>
              <div className="flex gap-2">
                <div className="text-xl font-semibold">
                  <DateRangePicker
                    showOneCalendar
                    format="dd/MM/yyyy"
                    value={range}
                    onChange={handleDateChange}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-orange-500 text-white py-2 px-3 border rounded"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-6 gap-10">
              <div className="bg-blue-200 ring-2 ring-blue-500 rounded-lg p-3 flex justify-between">
                <span className="font-semibold text-sm">Total</span>
                <span className="font-semibold">
                  {worklistCounter.map(
                    (td: any) =>
                      td?.received +
                      td?.processed +
                      td?.pending +
                      td?.exceptionagent +
                      td?.hold
                  )}
                </span>
              </div>
              <div className="bg-green-200 ring-2 ring-green-500 rounded-lg p-3  flex justify-between">
                <span className="font-semibold text-sm">Received</span>
                <span className="font-semibold">
                  {worklistCounter.map((td: any) => td?.received)}
                </span>
              </div>
              <div className="bg-yellow-200 ring-2 ring-yellow-500 rounded-lg p-3 flex justify-between">
                <span className="font-semibold text-sm">Processed</span>
                <span className="font-semibold">
                  {worklistCounter.map((td: any) => td?.processed)}
                </span>
              </div>
              <div className="bg-cyan-200 ring-2 ring-cyan-500 rounded-lg p-3 flex justify-between">
                <span className="font-semibold text-sm">Pending</span>
                <span className="font-semibold">
                  {worklistCounter.map((td: any) => td?.pending)}
                </span>
              </div>
              <div className="bg-red-200 ring-2 ring-red-500 rounded-lg p-3 flex justify-between">
                <span className="font-semibold text-sm">Exception</span>
                <span className="font-semibold">
                  {worklistCounter.map((td: any) => td?.exceptionagent)}
                </span>
              </div>
              <div className="bg-slate-200 ring-2 ring-gray-500 rounded-lg p-3 flex justify-between">
                <span className="font-semibold text-sm">Hold</span>
                <span className="font-semibold">
                  {worklistCounter.map((td: any) => td?.hold)}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-neutral-100 rounded-lg">
            <div className="px-2 flex gap-4 w-full border-b-2 border-b-solid border-b-neutral-200">
              {Object.keys(status as object).length > 0 ? (
                Object.keys(status as object).map((k, index) => (
                  <div
                    key={k}
                    className={`p-3 capitalize font-semibold cursor-pointer text-sm ${
                      index === activeTab
                        ? "border-b-2 border-b-solid border-b-red-500 text-sm text-red-500"
                        : ""
                    }`}
                    onClick={() => {
                      setActiveTab(index);
                      setActiveData(k);
                    }}
                  >
                    {k} ({status[k].length})
                  </div>
                ))
              ) : (
                <div className="p-3 font-semibold"></div>
              )}
            </div>
            <div className="p-2">
              {Boolean(status) &&
              activeData &&
              Boolean(status[activeData]) &&
              status[activeData].length > 0 ? (
                <SupervisorTable data={status[activeData]} />
              ) : (
                <div className="p-3 font-semibold">
                  No data available for the selected dates.
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        loading
      )}
    </div>
  );
};

export default WorklistComponent;
